import { useEffect, useRef } from 'react';

import { replaceLocation } from '../services/browser-navigation';
import { decideOfferEntry, type OfferEntryAction } from '../services/offer-entry.service';

/**
 * E-SF16 — hand a `/offer/:token` arrival off to `GET /bff/offer/:token`.
 *
 * The DECISION is `decideOfferEntry` (pure, tested on its own); this hook owns
 * only the effect — leaving the SPA — and the guarantee that it happens ONCE.
 *
 * ⚠️ THE ONCE-ONLY GUARD IS LOAD-BEARING, NOT TIDINESS. React 18 StrictMode
 * double-invokes effects in development, and each hand-off mints a FRESH funnel
 * session and consumes one of the customer's ten hourly offer-entry allowances. A
 * naive effect would burn two per open, and would do it only in dev — so the
 * symptom (a customer rate-limited off their own offer) would show up in
 * production traffic patterns nobody could reproduce locally.
 *
 * @returns the action taken, so the page can render the hand-off state or the
 *   single failure state. It never returns a reason for the failure — there is
 *   exactly one failure outcome by design (US-SF16.2).
 */
export function useOfferHandoff(token: string | undefined): OfferEntryAction {
  const action = decideOfferEntry(token);
  // A primitive dependency, not the action object — the object is rebuilt on every
  // render and would re-run the effect each time, leaving the ref below as the only
  // thing standing between the customer and a fresh session per render.
  const targetUrl = action.kind === 'handoff' ? action.url : null;
  const handedOff = useRef(false);

  useEffect(() => {
    if (targetUrl === null) return;
    if (handedOff.current) return;
    handedOff.current = true;
    replaceLocation(targetUrl);
  }, [targetUrl]);

  return action;
}
